'use client';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { usePathname, useRouter } from 'next/navigation';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useIsClient } from 'usehooks-ts';

import { type KeyGenFavorites } from '@/api/types/keyGenTypes';
import { uiConfig } from '@/config/uiConfig';
import { initializeAmplitude } from '@/features/analytics/amplitude/utils/experiment';
import { useAppsflyerBanner } from '@/features/analytics/appsflyer/useAppsflyerBanner';
import { useAppsflyerSmartScript } from '@/features/analytics/appsflyer/useAppsflyerSmartScript';
import { BRAZE_CUSTOM_ATTRIBUTES } from '@/features/analytics/braze/constants/brazeConstants';
import { useBrazeInstance } from '@/features/analytics/braze/hooks/useBrazeInstance';
import { beforeSendDatadog } from '@/features/analytics/datadog/utils';
import { triggerIdentifyEvent } from '@/features/analytics/events/utils/triggerIdentifyEvent';
import { getUserInfo, getXid, isOnlyCentralizedLoginAuthenticated } from '@/features/auth/client';
import { useUserInfo } from '@/features/auth/hooks/useUserInfo';
import { isMvpdAuthenticated } from '@/features/auth/isMvpdAuthenticated';
import { isTokenAuthenticated } from '@/features/auth/isTokenAuthenticated';
import { signIn, signInAnonymously } from '@/features/auth/signin';
import { useUserFavoriteIdsQuery } from '@/features/favorites/queries/useUserFavoriteIdsQuery';
import { useAsPathInitializer } from '@/hooks/useRouterHistory';
import { useUserEntitlementsQuery } from '@/queries/entitlements/useUserEntitlementsQuery';
import { useLocationQuery } from '@/queries/location/useLocationQuery';
import { useHomeLocationQuery } from '@/queries/preferences/useHomeLocationQuery';
import { useUserPreferencesQuery } from '@/queries/preferences/useUserPreferencesQuery';
import { useKeyGenQuery } from '@/queries/product/useKeyGenQuery';
import { getAuthSDK } from '@/sdk/client/getAuthSDK';
import { syncEventServiceAccountId } from '@/sdk/getEventServiceSDK';
import { useUserStore } from '@/stores/userStore';

declare global {
  interface Window {
    __APP_LAUNCH_FIRED_DMA?: string;

    __MVPD_PENDING?: boolean;
    __MVPD_FIRED?: boolean;

    __LOGIN_PENDING?: boolean;
    __LOGIN_FIRED?: boolean;
  }
}

export default function Initializer(): ReactNode {
  const { accessToken } = useUserInfo();

  const router = useRouter();
  const pathname = usePathname();

  const { data: homeLocation } = useHomeLocationQuery();
  const { data: userLocation } = useLocationQuery();
  const { data: userEntitlements } = useUserEntitlementsQuery();
  const { data: favoriteIds } = useUserFavoriteIdsQuery();
  const { data: userPreference } = useUserPreferencesQuery();
  const [xid, setXid] = useState<string>();
  const authSDK = getAuthSDK();
  const dma = userLocation?.metro_code;
  const dmaRef = useRef<string | undefined>(undefined);

  dmaRef.current = dma;

  const keygenFavorites: KeyGenFavorites = useMemo(() => {
    if (!favoriteIds) {
      return {};
    }

    return favoriteIds.reduce<KeyGenFavorites>((acc, entry) => {
      const [type, id] = entry.split(':', 2);

      if (type === 'series') {
        if (acc.series) {
          acc.series.push(id);
        } else {
          acc.series = [id];
        }
      }

      if (`${type}s` === 'teams') {
        if (acc.teams) {
          acc.teams.push(id);
        } else {
          acc.teams = [id];
        }
      }

      if (`${type}s` === 'leagues') {
        if (acc.leagues) {
          acc.leagues.push(id);
        } else {
          acc.leagues = [id];
        }
      }

      if (`${type}s` === 'specials') {
        if (acc.specials) {
          acc.specials.push(id);
        } else {
          acc.specials = [id];
        }
      }

      if (`${type}s` === 'movies') {
        if (acc.movies) {
          acc.movies.push(id);
        } else {
          acc.movies = [id];
        }
      }

      return acc;
    }, {});
  }, [favoriteIds]);

  const { appName, appVersion, subscribeUrl } = uiConfig;

  const entitlementsWithoutStartDate = useMemo(() => {
    return userEntitlements?.results.map(({ startDate: _, ...rest }) => {
      return {
        content_sku: rest.contentSku,
        proxied_entitlement: rest.proxiedEntitlement,
        entitlement_types: rest.entitlementType,
      };
    });
  }, [userEntitlements?.results]);

  const { data: keyGenHeaders } = useKeyGenQuery({
    user_entitlement: entitlementsWithoutStartDate,
    favorites: { ...keygenFavorites },
    user_onboarding_preferences: userPreference?.areas_interest,
  });

  const isClient = useIsClient();
  const setUserLocation = useUserStore((state) => state.setUserLocation);
  const setHomeLocation = useUserStore((state) => state.setHomeLocation);
  const setUserPreference = useUserStore((state) => state.setUserPreference);
  const setKeyGenHeaders = useUserStore((state) => state.setKeyGenHeaders);
  const setUserMetadata = useUserStore((state) => state.setUserMetadata);

  const { braze, changeUser } = useBrazeInstance();

  useAsPathInitializer();

  useEffect(() => {
    // TODO: We should move redirects to middleware/route handlers

    if (
      pathname.startsWith('/privacy-policy') ||
      pathname.startsWith('/terms-of-use') ||
      pathname.startsWith('/accessibility') ||
      pathname.startsWith('/ccpa/verify') ||
      pathname.startsWith('/callback') ||
      pathname.startsWith('/mvpd/success')
    ) {
      return;
    }

    if (userEntitlements && accessToken && isTokenAuthenticated(accessToken)) {
      const isLoggedInUserWithoutSubscription = !userEntitlements.parsedProps.isSubscribed;

      if (isLoggedInUserWithoutSubscription) {
        router.replace(subscribeUrl);
      }
    }
  }, [router, subscribeUrl, pathname, userEntitlements, accessToken]);

  useEffect(() => {
    async function initAnonymousLogin(): Promise<void> {
      if (pathname.startsWith('/callback')) {
        return;
      }

      const storedAccessToken = getUserInfo().accessToken;

      if (accessToken === undefined && !storedAccessToken) {
        await signInAnonymously();
      }
    }

    if (xid) {
      void initAnonymousLogin();
    }
  }, [pathname, xid, accessToken, authSDK, setUserMetadata]);

  useEffect(() => {
    void syncEventServiceAccountId();
  }, []);

  useAppsflyerSmartScript();

  useAppsflyerBanner();

  // so use effect only runs once even in dev mode
  const isFirstTime = useRef(true);
  const isFirstTimeDatadogRender = useRef(true);

  // keep track of current user id so we populate Braze profile using cookie data only once
  const currentUserId = useRef<string>(undefined);

  // Create random cache busting string only for local development to use in symbols.svg to avoid hard refreshes
  useEffect(() => {
    const isLocalDevelopment = process.env.NODE_ENV === 'development';

    if (isLocalDevelopment) {
      const randomString = Math.random().toString(36).substring(7);

      window.sessionStorage.setItem('cacheHash', randomString);
    }
  }, []);

  useEffect(() => {
    if (isFirstTime.current) {
      isFirstTime.current = false;
    }
  }, []);

  useEffect(() => {
    async function setInitialXid(): Promise<void> {
      const newXid = await getXid();

      setXid(newXid);
    }

    if (!xid) {
      void setInitialXid();
    }
  }, [xid]);

  useEffect(() => {
    async function storeUserMetadata(): Promise<void> {
      if (isMvpdAuthenticated(accessToken)) {
        const userMetadata = await authSDK.getUserMetadata({});

        setUserMetadata(userMetadata);
      }
    }

    void storeUserMetadata();
  }, [accessToken, authSDK, setUserMetadata]);

  useEffect(() => {
    async function initAmplitude(): Promise<void> {
      try {
        await initializeAmplitude();
      } catch (error) {
        console.error('Amplitude initialization error:', error);
      }
    }

    void initAmplitude();
  }, []);

  useEffect(() => {
    const { profileId } = getUserInfo();

    if (braze && profileId !== currentUserId.current) {
      currentUserId.current = profileId;
      changeUser(profileId);

      const brazeEnvFields = {
        [BRAZE_CUSTOM_ATTRIBUTES.APP_NAME]: appName,
        [BRAZE_CUSTOM_ATTRIBUTES.APP_VERSION]: appVersion,
        [BRAZE_CUSTOM_ATTRIBUTES.APP_PLATFORM]: 'web',
      } as Record<string, string>;

      Object.keys(brazeEnvFields).forEach((field: string) => {
        braze.getUser()?.setCustomUserAttribute(field, brazeEnvFields[field]);
      });
    }
  }, [braze, changeUser, appName, appVersion]);

  useEffect(() => {
    const { isDatadogEnabled } = uiConfig;

    if (!isClient || !isFirstTimeDatadogRender.current || !isDatadogEnabled) {
      return;
    }

    const { profileId } = getUserInfo();

    isFirstTimeDatadogRender.current = false;

    datadogRum.init({
      applicationId: '593f6ea2-f585-4029-9f65-0c2d8d1f4a8e',
      clientToken: 'pubff2dda946eb4229e6b8f7da6dee40a25',
      defaultPrivacyLevel: 'mask',
      enablePrivacyForActionName: true,
      env: uiConfig.environment,
      service: 'raptor-webapp-core',
      sessionReplaySampleRate: 100,
      sessionSampleRate: 100,
      // `site` refers to the Datadog site parameter of your organization
      // see https://docs.datadoghq.com/getting_started/site/
      site: 'datadoghq.com',
      silentMultipleInit: true,
      traceSampleRate: 100,
      trackLongTasks: true,
      trackResources: true,
      trackUserInteractions: true,
      version: uiConfig.appVersion,
      allowedTracingUrls: [(url) => url.startsWith(window.location.origin)],
      beforeSend: beforeSendDatadog,
    });

    if (profileId) {
      datadogRum.setUser({ profileId });
    }

    datadogLogs.init({
      clientToken: 'pubff2dda946eb4229e6b8f7da6dee40a25',
      site: 'datadoghq.com',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
    });
  }, [isClient]);

  useEffect(() => {
    setKeyGenHeaders(keyGenHeaders);
  }, [keyGenHeaders, setKeyGenHeaders]);

  useEffect(() => {
    setUserLocation(userLocation);
  }, [setUserLocation, userLocation]);

  useEffect(() => {
    setHomeLocation(homeLocation);
  }, [homeLocation, setHomeLocation]);

  useEffect(() => {
    setUserPreference(userPreference);
  }, [userPreference, setUserPreference]);

  /** Check every page for centralized login **/
  useEffect(() => {
    if (accessToken) {
      const isOnlyCentralizedLog = isOnlyCentralizedLoginAuthenticated(accessToken);

      if (isOnlyCentralizedLog) {
        if (pathname.startsWith('/callback')) {
          return;
        }

        void signIn();
      }
    }
  }, [pathname, accessToken]);

  useEffect(() => {
    if (!dmaRef.current) {
      return;
    }

    if (window.__APP_LAUNCH_FIRED_DMA === dmaRef.current) {
      return;
    }

    window.__APP_LAUNCH_FIRED_DMA = dmaRef.current;
    void triggerIdentifyEvent({ trigger: 'app_launch', dma: BigInt(dmaRef.current) });
  }, [dma]);

  useEffect(() => {
    const onMvpd = (): void => {
      window.__MVPD_PENDING = true;

      if (!window.__MVPD_FIRED && dmaRef.current) {
        window.__MVPD_FIRED = true;
        window.__MVPD_PENDING = false;
        void triggerIdentifyEvent({ trigger: 'mvpd_auth', dma: BigInt(dmaRef.current) });
      }
    };

    const onLogin = (): void => {
      window.__LOGIN_PENDING = true;

      if (!window.__LOGIN_FIRED && dmaRef.current) {
        window.__LOGIN_FIRED = true;
        window.__LOGIN_PENDING = false;
        void triggerIdentifyEvent({ trigger: 'login', dma: BigInt(dmaRef.current) });
      }
    };

    window.addEventListener('mvpd-auth-success', onMvpd);
    window.addEventListener('login-auth-success', onLogin);

    return () => {
      window.removeEventListener('mvpd-auth-success', onMvpd);
      window.removeEventListener('login-auth-success', onLogin);
    };
  }, []);

  useEffect(() => {
    if (!dma) {
      return;
    }

    if (window.__MVPD_PENDING && !window.__MVPD_FIRED) {
      window.__MVPD_FIRED = true;
      window.__MVPD_PENDING = false;
      void triggerIdentifyEvent({ trigger: 'mvpd_auth', dma: BigInt(dma) });
    }

    if (window.__LOGIN_PENDING && !window.__LOGIN_FIRED) {
      window.__LOGIN_FIRED = true;
      window.__LOGIN_PENDING = false;
      void triggerIdentifyEvent({ trigger: 'login', dma: BigInt(dma) });
    }
  }, [dma]);

  return null;
}
